import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Share, Check } from 'lucide-react';
import { HUAWEI_URL, GOOGLE_PLAY_URL } from '@/utils/storeRedirectStrings';

/**
 * ShareButton — shares BOTH the Google Play and Huawei AppGallery
 * listings so the recipient can pick the store that fits their device.
 * Previously the button captured a screenshot of the whole UI, which
 * was pretty but hid the real "download the app" intent behind an
 * opaque image. Sharing the two links is what stores, WhatsApp,
 * Telegram, X and Messenger all understand natively.
 */

// Localized description block. The two store URLs are appended in code
// (see handleShare) so brand names stay consistent worldwide.
const SHARE_TEXT = {
  en: 'Falak — an astronomical clock that shows the exact moment of the next crescent birth (to the second), prayer times, live Qibla and Hijri calendar. Free · 13 languages · No ads.',
  ar: 'فَلَك — ساعة فلكية تعرض اللحظة الدقيقة لولادة الهلال القادم (بدقة الثانية)، مواقيت الصلاة، بوصلة القبلة، والتقويم الهجري. مجاناً · 13 لغة · بلا إعلانات.',
  ur: 'Falak — ایک فلکیاتی گھڑی جو اگلے چاند کی پیدائش کا لمحہ (سیکنڈوں تک)، نمازوں کے اوقات، براہ راست قبلہ اور ہجری کیلنڈر دکھاتی ہے۔ مفت · 13 زبانیں۔',
  zh: 'Falak —— 一款天文时钟应用，可精确显示下个月新月诞生的时刻（精确到秒）、礼拜时间、朝向指南针和伊斯兰历。免费 · 13 种语言 · 无广告。',
  fa: 'فلک — یک ساعت نجومی که لحظه دقیق تولد هلال بعدی (تا ثانیه)، اوقات نماز، جهت قبله زنده و تقویم هجری را نمایش می‌دهد. رایگان · 13 زبان.',
  tr: 'Falak — sonraki hilalin doğuş anını saniye hassasiyetiyle gösteren astronomik saat uygulaması. Namaz vakitleri, kıble ve Hicri takvim. Ücretsiz · 13 dil · Reklamsız.',
  fr: "Falak — une horloge astronomique qui affiche l'instant précis de la naissance du prochain croissant (à la seconde), les heures de prière, la Qibla et le calendrier hégirien. Gratuit · 13 langues · Sans pub.",
  bn: 'Falak — একটি জ্যোতির্বিদ্যা ঘড়ি যা পরবর্তী চাঁদের জন্মের সঠিক মুহূর্ত (সেকেন্ড পর্যন্ত), নামাজের সময়, কিবলা এবং হিজরি ক্যালেন্ডার দেখায়। বিনামূল্যে · ১৩ ভাষা।',
  id: 'Falak — jam astronomi yang menampilkan momen persis kelahiran bulan sabit berikutnya (hingga detik), waktu shalat, arah kiblat, dan kalender Hijriah. Gratis · 13 bahasa · Tanpa iklan.',
  es: 'Falak — un reloj astronómico que muestra el momento exacto del próximo nacimiento del creciente (al segundo), horarios de oración, Qibla en vivo y calendario Hijri. Gratis · 13 idiomas · Sin anuncios.',
  pt: 'Falak — um relógio astronômico que mostra o momento exato do nascimento do próximo crescente (ao segundo), horários de oração, Qibla ao vivo e calendário Hijri. Grátis · 13 idiomas · Sem anúncios.',
  de: 'Falak — eine astronomische Uhr, die den exakten Moment der nächsten Mondsichel-Geburt (sekundengenau), Gebetszeiten, Live-Qibla und den Hidschri-Kalender anzeigt. Kostenlos · 13 Sprachen · Werbefrei.',
  it: 'Falak — un orologio astronomico che mostra il momento esatto della prossima nascita della falce di luna (al secondo), gli orari di preghiera, la Qibla in tempo reale e il calendario dell\'Egira. Gratis · 13 lingue · Senza pubblicità.',
};

const buildShareText = (lang) => {
  const description = SHARE_TEXT[lang] || SHARE_TEXT.en;
  return (
    `${description}\n\n` +
    `📱 Google Play: ${GOOGLE_PLAY_URL}\n` +
    `📱 AppGallery: ${HUAWEI_URL}`
  );
};

const ShareButton = ({ textColors }) => {
  const { t, i18n } = useTranslation();
  const [shared, setShared] = useState(false);

  const handleShare = async () => {
    const lang = i18n.language || 'en';
    const text = buildShareText(lang);

    try {
      if (navigator.share) {
        // Native share on mobile — WhatsApp, X, Messenger, Telegram, etc.
        await navigator.share({
          title: 'Falak',
          text,
          // Primary URL for previews — Google Play is the bigger audience
          url: GOOGLE_PLAY_URL,
        });
      } else {
        // Desktop fallback — copy to clipboard
        await navigator.clipboard.writeText(text);
      }
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    } catch (err) {
      // User cancelled — no console noise for that
      if (err && err.name !== 'AbortError') {
        console.error('Share failed:', err);
      }
    }
  };

  return (
    <button
      type="button"
      onClick={handleShare}
      className="px-4 py-2 rounded-lg backdrop-blur-xl border transition-all hover:-translate-y-1"
      style={{
        background: 'rgba(255, 255, 255, 0.2)',
        borderColor: 'rgba(255, 255, 255, 0.3)',
        color: textColors?.primary || '#FFFFFF',
      }}
      data-testid="share-button"
      aria-label={t('share')}
    >
      <div className="flex items-center gap-2">
        {shared ? <Check size={20} /> : <Share size={20} />}
        <span className="font-medium text-sm">
          {shared ? '✓' : t('share')}
        </span>
      </div>
    </button>
  );
};

export default ShareButton;
